import React, { useState } from 'react'
import { Turnstile } from '@marsidev/react-turnstile'
import { Button } from './ui/Button'

interface EmailSignupProps {
  productName?: string
}

export const EmailSignup: React.FC<EmailSignupProps> = ({ productName }) => {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileKey, setTurnstileKey] = useState(0)
  const [website, setWebsite] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!turnstileToken) {
      setError('Please complete the security check.')
      return
    }

    setLoading(true)

    try {
      const response = await fetch('/api/email-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          productName,
          turnstileToken,
          website
        })
      })

      // Clone response before reading to avoid "body stream already read" error
      const responseClone = response.clone()
      
      let data
      try {
        data = await response.json()
      } catch (parseError) {
        // If response is not JSON, get text from cloned response
        const text = await responseClone.text()
        throw new Error(`Server error: ${text || response.statusText}`)
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sign up')
      }

      // Success
      setSubmitted(true)
      setEmail('')
      setTurnstileToken(null)
      setTurnstileKey((value) => value + 1)
      setTimeout(() => {
        setSubmitted(false)
      }, 3000)
    } catch (err) {
      setTurnstileToken(null)
      setTurnstileKey((value) => value + 1)
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          id="email-signup"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          autoComplete="email"
          required
          className="flex-1 px-4 py-3 border border-stone/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-midnight-black focus:border-transparent text-[19px]"
        />
        <input
          type="text"
          name="website"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] h-px w-px overflow-hidden"
        />
        <Button
          type="submit"
          disabled={loading || submitted}
          size="md"
        >
          {loading ? 'Sending...' : submitted ? 'Thanks' : 'Submit'}
        </Button>
      </div>
      <Turnstile
        key={turnstileKey}
        siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || ''}
        onSuccess={setTurnstileToken}
        onExpire={() => setTurnstileToken(null)}
        onError={() => {
          setTurnstileToken(null)
          setError('The security check could not load. Please refresh and try again.')
        }}
        options={{ theme: 'light', size: 'flexible' }}
      />
      {error && (
        <p className="text-[19px] text-red-600 font-medium">
          {error}
        </p>
      )}
      {submitted && !error && (
        <p className="text-[19px] text-midnight-blue/60 font-medium">
          Thanks for your interest. We'll be in touch.
        </p>
      )}
    </form>
  )
}
